import React, { useState } from "react";
import SubPageLayout from "../../components/shared/SubPageLayout";

const CurrentLicenses = () => {
  const breadcrumbItems = [
    { label: "Education & Licensing", link: "/education-licensing" },
    {
      label: "Licenses & Insurance",
      link: "/education-licensing/licenses-insurance",
    },
  ];

  // Mock data for licenses
  const [licenses, setLicenses] = useState([
    {
      id: 1,
      type: "Series 7",
      state: "NY",
      status: "Active",
      expirationDate: "2025-12-31",
      renewalDeadline: "2025-11-30",
      ceCreditsRequired: 30,
      ceCreditsCompleted: 15,
      priority: "Low",
    },
    {
      id: 2,
      type: "Series 66",
      state: "CA",
      status: "Active",
      expirationDate: "2025-06-30",
      renewalDeadline: "2025-05-31",
      ceCreditsRequired: 24,
      ceCreditsCompleted: 12,
      priority: "Medium",
    },
    {
      id: 3,
      type: "Insurance Producer",
      state: "FL",
      status: "Pending Renewal",
      expirationDate: "2024-04-30",
      renewalDeadline: "2024-03-31",
      ceCreditsRequired: 20,
      ceCreditsCompleted: 5,
      priority: "High",
    },
    {
      id: 4,
      type: "Series 63",
      state: "TX",
      status: "Expired",
      expirationDate: "2024-01-31",
      renewalDeadline: "2024-01-01",
      ceCreditsRequired: 15,
      ceCreditsCompleted: 0,
      priority: "Critical",
    },
  ]);

  // Mock data for insurance coverage
  const [insuranceCoverage] = useState({
    type: "Errors & Omissions",
    provider: "Professional Insurance Corp",
    policyNumber: "EO123456789",
    coverage: "$2,000,000",
    deductible: "$5,000",
    expirationDate: "2025-12-31",
    premium: "$2,400/year",
  });

  // Mock data for recommended actions
  const [recommendedActions] = useState([
    {
      id: 1,
      action: "Complete CE Credits for Insurance Producer License",
      deadline: "2024-03-15",
      priority: "High",
      type: "CE",
    },
    {
      id: 2,
      action: "Renew Series 63 License",
      deadline: "2024-02-28",
      priority: "Critical",
      type: "Renewal",
    },
    {
      id: 3,
      action: "Review E&O Insurance Coverage",
      deadline: "2024-06-30",
      priority: "Medium",
      type: "Insurance",
    },
  ]);

  const [activeTab, setActiveTab] = useState("overview");

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800 dark:text-gray-200";
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "pending renewal":
        return "bg-yellow-100 text-yellow-800";
      case "expired":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800 dark:text-gray-200";
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Total Licenses</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-gray-100">
            {licenses.length}
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Active Licenses</h3>
          <p className="mt-2 text-3xl font-semibold text-green-600">
            {licenses.filter((l) => l.status === "Active").length}
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Pending Renewals
          </h3>
          <p className="mt-2 text-3xl font-semibold text-yellow-600">
            {licenses.filter((l) => l.status === "Pending Renewal").length}
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Expired</h3>
          <p className="mt-2 text-3xl font-semibold text-red-600">
            {licenses.filter((l) => l.status === "Expired").length}
          </p>
        </div>
      </div>

      {/* Recommended Actions */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Recommended Actions
          </h2>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {recommendedActions.map((action) => (
              <div
                key={action.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-2 h-2 rounded-full ${getPriorityColor(action.priority)}`}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {action.action}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      Due by {new Date(action.deadline).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(action.priority)}`}
                >
                  {action.priority}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderLicensesTab = () => (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                License
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                State
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Expiration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                CE Progress
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
            {licenses.map((license) => (
              <tr key={license.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {license.type}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">{license.state}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(license.status)}`}
                  >
                    {license.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {new Date(license.expirationDate).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    Renewal by{" "}
                    {new Date(license.renewalDeadline).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{
                        width: `${(license.ceCreditsCompleted / license.ceCreditsRequired) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {license.ceCreditsCompleted}/{license.ceCreditsRequired}{" "}
                    Credits
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(license.priority)}`}
                  >
                    {license.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button className="text-[#202EC6] hover:text-[#202EC6] mr-4">
                    Renew
                  </button>
                  <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100">
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderInsuranceTab = () => (
    <div className="space-y-6">
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            E&O Insurance Coverage
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-500">Provider</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {insuranceCoverage.provider}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-500">Policy Number</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {insuranceCoverage.policyNumber}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-500">
                Coverage Amount
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {insuranceCoverage.coverage}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-500">Deductible</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {insuranceCoverage.deductible}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-500">
                Expiration Date
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {new Date(
                  insuranceCoverage.expirationDate,
                ).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-500">
                Annual Premium
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {insuranceCoverage.premium}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Additional Coverage Options
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Cyber Liability Insurance
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Protect against data breaches and cyber threats
              </p>
              <button className="text-[#202EC6] hover:text-opacity-80 text-sm font-medium">
                Learn More →
              </button>
            </div>
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Professional Liability Insurance
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Additional coverage for professional services
              </p>
              <button className="text-[#202EC6] hover:text-opacity-80 text-sm font-medium">
                Learn More →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <SubPageLayout
      title="Licenses & Insurance"
      subtitle="Manage your licenses, track renewals, and maintain compliance"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-6">
        {/* Navigation Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-4">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === "overview"
                  ? "bg-[#202EC6] text-white"
                  : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("licenses")}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === "licenses"
                  ? "bg-[#202EC6] text-white"
                  : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
              }`}
            >
              Licenses
            </button>
            <button
              onClick={() => setActiveTab("insurance")}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === "insurance"
                  ? "bg-[#202EC6] text-white"
                  : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
              }`}
            >
              Insurance Coverage
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && renderOverviewTab()}
        {activeTab === "licenses" && renderLicensesTab()}
        {activeTab === "insurance" && renderInsuranceTab()}
      </div>
    </SubPageLayout>
  );
};

export default CurrentLicenses;
